/**
 * useGeoSEO — Direct DOM meta tag injection for geo SEO pages.
 * 
 * This runs synchronously during render (via useEffect with immediate execution)
 * to update all meta tags BEFORE the edge renderer captures the page.
 * 
 * Addresses the exact issue Emergent support identified: when the SPA has
 * hardcoded meta tags in index.html, we need to REPLACE them (not add duplicates).
 */
import { useEffect } from 'react';

const SITE_URL = 'https://localbrandingpro.com';

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) {
    el.setAttribute(attr, value);
  } else {
    // Create the tag if it doesn't exist
    const meta = document.createElement('meta');
    if (selector.includes('property=')) {
      const prop = selector.match(/property="([^"]+)"/)?.[1];
      if (prop) meta.setAttribute('property', prop);
    } else if (selector.includes('name=')) {
      const name = selector.match(/name="([^"]+)"/)?.[1];
      if (name) meta.setAttribute('name', name);
    }
    meta.setAttribute(attr, value);
    document.head.appendChild(meta);
  }
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (link) {
    link.setAttribute('href', href);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', href);
    document.head.appendChild(link);
  }
}

function setHreflang(lang, href) {
  let link = document.querySelector(`link[hreflang="${lang}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function addJsonLd(schema, id) {
  // Remove existing script with this id if present
  const existing = document.querySelector(`script[data-geo-schema="${id}"]`);
  if (existing) existing.remove();

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-geo-schema', id);
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

function cleanupJsonLd() {
  document.querySelectorAll('script[data-geo-schema]').forEach(el => el.remove());
}

export default function useGeoSEO(page) {
  useEffect(() => {
    if (!page) return;

    const content = page.content || {};
    const city = page.city_name || '';
    const stateAbbr = page.state_abbr || '';
    const stateName = page.state_name || '';
    const serviceName = page.service_name || '';
    const stateSlug = page.state_slug || '';
    const citySlug = page.city_slug || '';
    const serviceId = page.service_id || '';

    const title = content.meta_title || `${serviceName} in ${city}, ${stateAbbr} | Local Branding Pro`;
    const desc = content.meta_description || `Professional ${serviceName} services for businesses in ${city}, ${stateAbbr}.`;
    const keywords = (content.longtail_keywords || []).join(', ');
    const canonical = `${SITE_URL}/api/seo/page/${stateSlug}/${citySlug}/${serviceId}.html`;
    const pageUrl = `${SITE_URL}/locations/${stateSlug}/${citySlug}/${serviceId}`;

    // 1. Document title
    document.title = title;

    // 2. Standard meta tags — REPLACE existing ones
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[name="robots"]', 'content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);

    // 3. Geo meta tags
    setMeta('meta[name="geo.region"]', 'content', `US-${stateAbbr}`);
    setMeta('meta[name="geo.placename"]', 'content', city);

    // 4. Open Graph — REPLACE existing ones
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[property="og:site_name"]', 'content', 'Local Branding Pro');
    setMeta('meta[property="og:locale"]', 'content', 'en_US');

    // 5. Twitter Card — REPLACE existing ones
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', desc);

    // 6. Canonical — REPLACE existing
    setCanonical(canonical);

    // 7. Hreflang
    setHreflang('en-us', canonical);
    setHreflang('x-default', canonical);

    // 8. JSON-LD Schemas (add new ones with data-geo-schema for cleanup)
    addJsonLd({
      "@context": "https://schema.org", "@type": "LocalBusiness",
      "name": `Local Branding Pro — ${serviceName} in ${city}`,
      "description": desc, "url": pageUrl, "telephone": "+18179180589",
      "email": "info@localbrandingpro.com",
      "areaServed": { "@type": "City", "name": city, "containedInPlace": { "@type": "State", "name": stateName } },
      "address": { "@type": "PostalAddress", "addressLocality": city, "addressRegion": stateAbbr, "addressCountry": "US" },
    }, 'local-business');

    addJsonLd({
      "@context": "https://schema.org", "@type": "Service",
      "name": `${serviceName} in ${city}, ${stateAbbr}`, "description": desc, "url": pageUrl,
      "provider": { "@type": "Organization", "name": "Local Branding Pro", "url": SITE_URL, "telephone": "+18179180589" },
      "areaServed": { "@type": "City", "name": city }, "serviceType": serviceName,
    }, 'service');

    addJsonLd({
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Locations", "item": `${SITE_URL}/locations` },
        { "@type": "ListItem", "position": 3, "name": stateAbbr, "item": `${SITE_URL}/locations/${stateSlug}` },
        { "@type": "ListItem", "position": 4, "name": city, "item": `${SITE_URL}/locations/${stateSlug}/${citySlug}` },
        { "@type": "ListItem", "position": 5, "name": serviceName },
      ],
    }, 'breadcrumb');

    addJsonLd({
      "@context": "https://schema.org", "@type": "HowTo",
      "name": `How to Choose a ${serviceName} Provider in ${city}`,
      "description": `A step-by-step guide for ${city} businesses selecting a ${serviceName} partner.`,
      "totalTime": "PT15M",
      "step": [
        { "@type": "HowToStep", "position": 1, "name": `Define your ${serviceName} goals`, "text": `List 2-3 specific outcomes you want from ${serviceName} in ${city}.` },
        { "@type": "HowToStep", "position": 2, "name": `Verify local ${city} experience`, "text": `Ask for case studies from ${city} or nearby cities.` },
        { "@type": "HowToStep", "position": 3, "name": "Check reviews and credentials", "text": `Look for verified Google reviews and a portfolio of completed work for ${city} businesses.` },
        { "@type": "HowToStep", "position": 4, "name": "Request a written strategy", "text": `A reputable ${serviceName} provider in ${city} will deliver a written 30/60/90-day strategy.` },
        { "@type": "HowToStep", "position": 5, "name": "Confirm reporting and ownership", "text": "Make sure you own all accounts and receive monthly performance reports." },
      ],
    }, 'howto');

    addJsonLd({
      "@context": "https://schema.org", "@type": "WebPage", "url": canonical,
      "speakable": { "@type": "SpeakableSpecification", "cssSelector": [".hero h1", ".hero .intro", ".faq summary", ".voice-search-answer"] },
    }, 'speakable');

    addJsonLd({
      "@context": "https://schema.org", "@type": "Article",
      "headline": title, "description": desc, "url": canonical,
      "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
      "author": { "@type": "Organization", "name": "Local Branding Pro", "url": SITE_URL },
      "publisher": { "@type": "Organization", "name": "Local Branding Pro", "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.png` } },
      "datePublished": page.generated_at, "dateModified": page.generated_at,
      "inLanguage": "en-US", "isAccessibleForFree": true,
    }, 'article');

    const faqs = content.faq || [];
    if (faqs.length > 0) {
      addJsonLd({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question", "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      }, 'faq');
    }

    // Cleanup on unmount — remove geo-specific tags only.
    // RouteMetaManager handles setting new defaults on navigation.
    return () => {
      document.querySelector('meta[name="geo.region"]')?.remove();
      document.querySelector('meta[name="geo.placename"]')?.remove();
      document.querySelector('meta[name="keywords"]')?.remove();
      document.querySelector('link[hreflang="en-us"]')?.remove();
      document.querySelector('link[hreflang="x-default"]')?.remove();
      cleanupJsonLd();
    };
  }, [page]);
}
